import * as amplitude from '@amplitude/analytics-browser';
import { AmplitudeReturn } from '@amplitude/analytics-browser/lib/esm/types';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { APP_CONFIG, AppConfig } from '@src/app/app-config.module';
import { NGXLogger } from 'ngx-logger';
import { FormErrorsSummary } from '../formHelpers';
import { ApplicationSegment, Company, GeolocationOutcome, UBOProvisioningChoice, WebappInitializationData } from '../models/data-model';
import { SoldoAffiliateData, SoldoCookieService, SoldoMarketingParameters } from './soldo-cookie.service';
import { CompanySearchError, CompanySearchMode } from './company-search.service';

export type AmplitudeEventCategory = "Navigation" | "Milestone" | "Interaction" | "Integration" | "Error";

export type RegistrationStartRejectedReason = "invalid-plan-offer" | "user-chose-new-plan";

export interface AmplitudeEventProperties {
    app?: "Registration Webapp";
    category: AmplitudeEventCategory,
    subcategory?: string,
    onPage?: string,
    browserLanguage?: string,
    appLanguage?: string,
    selectedPlan?: string,
    selectedOffer?: string,
    [key: string]: any
}

@Injectable()
export class AmplitudeService {

    private readonly defaultProperties: AmplitudeEventProperties = {
        app: "Registration Webapp",
        category: "Navigation",
    };

    private uniquenessHelper: object = {};
    private submissionAttempts: { [pageName: string]: number } = {};

    private affiliateParams: SoldoAffiliateData;
    private utmParams: SoldoMarketingParameters;

    private readonly CATEGORY_ADDRESS_FORM = "Address Form";
    private readonly CATEGORY_COMPANY_SEARCH = "Company Search";

    constructor(
        private readonly cookieSerivice: SoldoCookieService,
        private readonly translate: TranslateService,
        private readonly logger: NGXLogger,
        @Inject(APP_CONFIG) private readonly appConfig: AppConfig
    ) {
    }

    initAmplitude(appVersion: string) {
        try {
            this.logger.debug("Initializing Amplitude with", this.appConfig.amplitudeProjectId);

            amplitude.init(this.appConfig.amplitudeProjectId, {
                userId: undefined,
                autocapture: false,
                appVersion: appVersion,
                logLevel: this.appConfig.env === 'prod' ? amplitude.Types.LogLevel.Error : amplitude.Types.LogLevel.Warn
            }).promise.then(() => {
                this.logger.debug("Amplitude initialized, running post init setup");
                this.postInit();
            }).catch((e) => {
                this.logger.debug("Couldn't initialize Amplitude", e);
            });

        } catch (e) {
            this.logger.debug("Couldn't initialize Amplitude", e);
        }
    }


    private postInit() {
        this.affiliateParams = this.cookieSerivice.readDedicatedAffiliateTrackingCookie();
        this.utmParams = this.cookieSerivice.readDedicatedUTMCookie();
    }

    setSalesforceExternalId(id: string) {
        amplitude.setUserId(id);
    }

    setUserMetrics(metrics: { [key: string]: any }): void {
        const identifyEvent = new amplitude.Identify();
        for (const k in metrics) {
            identifyEvent.set(k, metrics[k]);
        }
        amplitude.identify(identifyEvent);
    }

    setWebappChecklist(webappCheckList: string[]): void {
        this.defaultProperties.webappCheckList = webappCheckList;
    }

    setPlanAndOffer(plan: string, offer: string): void {
        this.defaultProperties.selectedPlan = plan;
        this.defaultProperties.selectedOffer = offer;
    }

    private eventProperties(customProperties: AmplitudeEventProperties): AmplitudeEventProperties {
        const defaultProperties: AmplitudeEventProperties = {
            ...this.defaultProperties,
            browserLanguage: this.translate.getBrowserCultureLang(),
            appLanguage: this.translate.getCurrentLang(),
            previousPages: document.referrer
        };

        if (this.utmParams) {
            for (let param of Object.keys(this.utmParams)) {
                defaultProperties[param] = this.utmParams[param];
            }
        }
        if (this.affiliateParams) {
            defaultProperties.affiliateId = this.affiliateParams.affiliateId;
        }
        return { ...defaultProperties, ...customProperties };
    }

    trackLanguageChange(language: string, fromLanguage: string, onPage: string): void {
        amplitude.track('Language Changed', this.eventProperties({
            category: 'Interaction',
            appLanguage: language,
            fromLanguage: fromLanguage,
            onPage: onPage
        }));
    }

    trackPageView(routeSnapshot: ActivatedRouteSnapshot): void {
        this.logger.debug("Amplitude - tracking page view", routeSnapshot.data['pageName'], this.defaultProperties.webappChecklist);
        const title = routeSnapshot.data['pageName'] || location.hash.replace('#', '');

        const path = location.hash.replace('#', '');

        this.submissionAttempts[title] = 0;

        if (path != "/") {
            amplitude.track(`Viewed ${title}`, this.eventProperties({
                category: "Navigation"
            }));
        }
    }

    trackWebappInitialized(initData: WebappInitializationData): void {
        amplitude.track("Registration Webapp Init", this.eventProperties({
            category: "Milestone",
            planName: initData.planName,
            offerName: initData.offerName,
            isXero: Boolean(initData.isXero),
            hasDiscountCode: Boolean(initData.hasDiscountCode),
            languageResolvedFrom: initData.languageResolvedFrom
        }));
    }

    trackNewRegistrationStarted(segment: string): void {
        this.setUserMetrics({
            segment: segment
        });
        amplitude.track("New Registration Started", this.eventProperties({
            category: "Milestone"
        }));
    }

    trackSignupCountryAccepted(countryOfIncorporation: string) {
        const eventName = "Selected Country of Incorporation";
        if (!this.uniquenessHelper[eventName]) {
            this.setUserMetrics({
                companyCountry: countryOfIncorporation
            });
            amplitude.track(eventName, this.eventProperties({
                category: "Navigation"
            }));
            this.uniquenessHelper[eventName] = true;
        }
    }

    trackGeolocationResolved(geolocatedCountry: string | null, outcome: GeolocationOutcome,
        geolocatedCountrySupported: boolean | null): void {
        const eventName = "Geolocation Resolved";
        if (this.uniquenessHelper[eventName]) {
            return;
        }
        this.setUserMetrics({
            geolocatedCountry: geolocatedCountry,
            geolocationOutcome: outcome,
            geolocatedCountrySupported: geolocatedCountrySupported
        });
        amplitude.track(eventName, this.eventProperties({
            category: outcome === 'failed' || outcome === 'unmapped' ? "Error" : "Milestone",
            geolocatedCountry: geolocatedCountry,
            geolocationOutcome: outcome,
            geolocatedCountrySupported: geolocatedCountrySupported
        }));
        this.uniquenessHelper[eventName] = true;
    }

    trackUnsupportedCountrySelected(iso3: string, reason: 'not-active-market' | 'blacklisted', geolocatedCountry?: string): void {
        amplitude.track("Unsupported Country Selected", this.eventProperties({
            category: "Error",
            iso3,
            reason,
            geolocatedCountry
        }));
    }

    trackSignupPrivacyAccepted() {
        const eventName = "Accepted Privacy Policy";
        if (!this.uniquenessHelper[eventName]) {
            amplitude.track(eventName, this.eventProperties({
                category: "Navigation"
            }));
            this.uniquenessHelper[eventName] = true;
        }
    }

    trackSignupCompleted(company: Company): void {
        const companyType = company.type;
        this.setUserMetrics({
            companyCountry: company.registeredAddress?.countryISO,
            numberOfEmployees: company.employees,
            companyType: companyType
        });

        amplitude.track("Signup Completed", this.eventProperties({
            category: "Milestone",
            numberOfEmployees: company.employees,
            market: company.registeredAddress?.countryISO
        }));
    }

    trackApplicationResumed(segment: ApplicationSegment): void {
        this.setUserMetrics({
            segment: segment
        });
        amplitude.track("Application Resumed", this.eventProperties({
            category: "Milestone"
        }));
    }

    trackApplicationCompleted(): void {
        amplitude.track("Application Completed", this.eventProperties({
            category: "Milestone"
        }));
    }

    trackApplicationAbandoned(): void {
        amplitude.track("Application Abandoned", this.eventProperties({
            category: "Milestone"
        }));
    }

    trackFormSubmissionRejected(pageName: string, { invalidFields, asyncValidatorPending }: FormErrorsSummary): void {
        this.submissionAttempts[pageName] = (this.submissionAttempts[pageName] ?? 0) + 1;

        amplitude.track("Form Submission Rejected", this.eventProperties({
            category: "Error",
            onPage: pageName,
            invalidFields: invalidFields,
            attemptNumber: this.submissionAttempts[pageName],
            asyncValidatorPending: asyncValidatorPending
        }));
    }

    trackSubStepViewed(subStepName: string) {
        amplitude.track("SubStep Viewed", this.eventProperties({
            category: "Navigation",
            subStepName: subStepName
        }));
    }

    trackSubStepCompleted(subStepName: string) {
        amplitude.track("SubStep Completed", this.eventProperties({
            category: "Navigation",
            subStepName: subStepName
        }));
    }

    trackEmailVerificationCodeRequested() {
        amplitude.track("Email Verification Code Requested", this.eventProperties({
            category: "Interaction"
        }));
    }

    trackEmailVerified(fastVerification = false) {
        amplitude.track("Email Verified", this.eventProperties({
            category: "Interaction",
            fastVerification: fastVerification
        }));
    }

    trackOneTimeCodeRejected() {
        amplitude.track("One Time Code Verification Rejected", this.eventProperties({
            category: "Interaction"
        }));
    }

    trackFreeEmailDomainErrorTriggered(emailProvider: string) {
        amplitude.track("Free Email Domain Error Triggered", this.eventProperties({
            category: "Interaction",
            domain: emailProvider
        }));
    }

    trackAddressSearchPerformed(page: string, country: string, numResults: number, noActionableResult: boolean): void {
        amplitude.track("Address Search Query", this.eventProperties({
            category: "Interaction",
            subcategory: this.CATEGORY_ADDRESS_FORM,
            onPage: page,
            addressCountry: country,
            numResults: numResults,
            noActionableResult: noActionableResult,
        }));
    }

    trackAddressSearchProviderFailed(page: string, country: string): void {
        amplitude.track("Address Search Provider Failed", this.eventProperties({
            category: "Interaction",
            subcategory: this.CATEGORY_ADDRESS_FORM,
            onPage: page,
            addressCountry: country,
        }));
    }

    updateCompanySearchUsageMetric(companySearchResultSubmitted: boolean) {
        this.setUserMetrics({
            usedCompanySearch: companySearchResultSubmitted
        });
    }

    trackCompanySearchPerformed(searchedTermLength: number, numResults: number, searchByNumber: boolean = false): void {
        amplitude.track("Company Search Query", this.eventProperties({
            category: "Interaction",
            subcategory: this.CATEGORY_COMPANY_SEARCH,
            numResults: numResults,
            searchByNumber: searchByNumber,
            searchTermLenth: searchedTermLength
        }));
    }

    trackCompanySearchFailed(errorType: CompanySearchError, searchMode: CompanySearchMode, searchedTermLength: number, countryIso3: string): void {
        amplitude.track("Company Search Failed", this.eventProperties({
            category: "Error",
            subcategory: this.CATEGORY_COMPANY_SEARCH,
            errorType: errorType,
            searchMode: searchMode,
            searchTermLength: searchedTermLength,
            countryIso3: countryIso3
        }));
    }

    trackCompanyLookupFailed(countryIso3: string): void {
        amplitude.track("Company Lookup Failed", this.eventProperties({
            category: "Error",
            subcategory: this.CATEGORY_COMPANY_SEARCH,
            countryIso3: countryIso3
        }));
    }

    trackCompanySearchResultDiscarded(countryIso3: string): void {
        amplitude.track("Company Search Result Discarded", this.eventProperties({
            category: "Interaction",
            subcategory: this.CATEGORY_COMPANY_SEARCH,
            countryIso3: countryIso3
        }));
    }

    trackCompanyInputModeSwitched(countryIso3: string, direction: 'toManual' | 'toSearch'): void {
        amplitude.track("Company Input Mode Switched", this.eventProperties({
            category: "Interaction",
            subcategory: this.CATEGORY_COMPANY_SEARCH,
            countryIso3: countryIso3,
            direction: direction
        }));
    }

    trackSignupWithXeroAuthorized(): void {
        amplitude.track("Signup With Xero Authorized", this.eventProperties({
            category: "Integration",
            subcategory: "Xero"
        }));
    }

    trackBeneficialOwnerInputModeChoice(mode: UBOProvisioningChoice): void {
        amplitude.track("Beneficial Owner Input Mode Selected", this.eventProperties({
            category: "Interaction",
            subcategory: "Beneficial Owner",
            chosenInputMode: mode
        }));
    }

    trackBeneficialOwnersCreated(total: number, isDirector: boolean, totAdmin: number, totAdditional: number): void {
        amplitude.track("Beneficial Owners Saved", this.eventProperties({
            category: "Interaction",
            subcategory: "Beneficial Owner",
            totBeneficialOwners: total,
            totDirectorBO: isDirector ? 1 : 0,
            totAdminBO: totAdmin,
            totAdditional: totAdditional
        }));
    }

    trackLegalRepresentativeChoice(isApplicant: boolean): void {
        amplitude.track("Legal Representative Choice", this.eventProperties({
            category: "Interaction",
            isApplicant: isApplicant
        }));

        this.setUserMetrics({ applicantIsLegalRepresentative: isApplicant });
    }

    trackAccountAdminChoice(isApplicant: boolean, isLegalRepresentative: boolean): void {
        amplitude.track("Account Admin Choice", this.eventProperties({
            category: "Interaction",
            isApplicant: isApplicant,
            isLegalRepresentative: isLegalRepresentative
        }));

        this.setUserMetrics({
            applicantIsAdmin: isApplicant,
            legalRepresentativeIsAdmin: isLegalRepresentative
        });
    }

    trackFreeTextSurveyOptionSubmitted(subStepName: string): void {
        amplitude.track("Free Text Survey Option Submitted", this.eventProperties({
            category: "Interaction",
            subStepName: subStepName
        }));
    }

    trackFreeTextSurveyOptionSelected(subStepName: string): void {
        amplitude.track("Free Text Survey Option Selected", this.eventProperties({
            category: "Interaction",
            subStepName: subStepName
        }));
    }

    trackSaveAndContinueClicked(stepName: string, substepName: string): void {
        amplitude.track("Clicked Save and Continue", this.eventProperties({
            category: "Interaction",
            step: stepName,
            substep: substepName
        }));
    }

    trackOnfidoWebSdkTraversal(idvStepName: string, isCrossDevice: boolean): void {
        amplitude.track("Onfido Web IDV Traversal", this.eventProperties({
            category: "Interaction",
            subcategory: "Identity Verification",
            idvStepName: idvStepName,
            isCrossDevice: isCrossDevice
        }));
    }

    trackOnfidoWebSdkError(errorType: string): void {
        amplitude.track("Onfido Web IDV Error", this.eventProperties({
            category: "Error",
            subcategory: "Identity Verification",
            errorType: errorType
        }));
    }

    trackOnfidoWebSdkIdvUserExit(): void {
        amplitude.track("Onfido Web IDV User Exit", this.eventProperties({
            category: "Interaction",
            subcategory: "Identity Verification"
        }));
    }

    trackOnfidoWebSdkIdvComplete(): void {
        amplitude.track("Onfido Web IDV Completed", this.eventProperties({
            category: "Interaction",
            subcategory: "Identity Verification"
        }));
    }

    public trackInternalUserIdvStarted(): void {
        amplitude.track("Internal User IDV Started", this.eventProperties({
            category: "Interaction",
            subcategory: "Identity Verification"
        }));
    }

    public trackInternalUserIdvFormSubmitted(): void {
        amplitude.track("Internal User IDV Form Submitted", this.eventProperties({
            category: "Interaction",
            subcategory: "Identity Verification"
        }));
    }

    public trackInternalUserIdvResumed(): void {
        amplitude.track("Internal User IDV Resumed", this.eventProperties({
            category: "Interaction",
            subcategory: "Identity Verification"
        }));
    }

    public trackInternalUserIdvCompleted(): void {
        amplitude.track("Internal User IDV Completed", this.eventProperties({
            category: "Interaction",
            subcategory: "Identity Verification"
        }));
    }

    public trackInternalUserIdvError(expiredToken:boolean): void {
        amplitude.track("Internal User IDV Error", this.eventProperties({
            expiredToken: expiredToken,
            category: "Interaction",
            subcategory: "Identity Verification"
        }));
    }

    trackDotfileLinkNotAvailable(): void {
        amplitude.track("Dotfile Portal Link Unavailable", this.eventProperties({
            category: "Error",
            subcategory: "Identity Verification"
        }));
    }

    trackOpenDotfilePortal(): AmplitudeReturn<void> {
        amplitude.track("Dotfile Verification Portal Open", this.eventProperties({
            category: "Interaction",
            subcategory: "Identity Verification"
        }));
        return amplitude.flush();
    }

    trackBundleCodeRejected(planName: string, offerName: string): AmplitudeReturn<void> {
        amplitude.track("Plan or Offer id Rejected", this.eventProperties({
            category: "Error",
            subcategory: "Plan Selection",
            planName: planName,
            offerName: offerName
        }));
        return amplitude.flush();
    }

    trackBackToPlanSelection(planName: string, offerName: string): AmplitudeReturn<void> {
        amplitude.track("Back to Plan Selection", this.eventProperties({
            category: "Interaction",
            subcategory: "Plan Selection",
            planName: planName,
            offerName: offerName
        }));
        return amplitude.flush();
    }
}
